import { API_BASE } from '@/utils/api-url'

/**
 * 发起官网 API 请求；FormData 请求由浏览器自动生成上传边界。
 * @param {string} path API 路径
 * @param {object} options fetch 请求配置
 * @returns {Promise<*>} 业务数据
 */
async function request(path, options = {}) {
  const token = localStorage.getItem('dzzj_website_token')
  const { headers: optionHeaders = {}, ...requestOptions } = options
  const defaultHeaders = requestOptions.body instanceof FormData
    ? {}
    : { 'Content-Type': 'application/json' }
  const response = await fetch(`${API_BASE}${path}`, {
    ...requestOptions,
    headers: {
      ...defaultHeaders,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...optionHeaders
    }
  })
  const result = await response.json()
  if (response.status === 424 || result.code === 424) {
    if (token) {
      localStorage.removeItem('dzzj_website_token')
      localStorage.removeItem('dzzj_website_refresh_token')
      window.dispatchEvent(new CustomEvent('website-auth-expired'))
      throw new Error('登录已失效，请重新登录')
    }
    throw new Error(result.msg || '接口请求失败')
  }
  if (!response.ok || result.code !== 0) {
    throw new Error(result.msg || '接口请求失败')
  }
  return result.data
}

export function createWebsiteOrder(data, idempotencyKey) {
  return request('/trade/website/orders', {
    method: 'POST',
    headers: {
      'Idempotency-Key': idempotencyKey
    },
    body: JSON.stringify(data)
  })
}

export function reviseWebsiteOrder(id, data) {
  return request(`/trade/website/orders/${id}/revision`, {
    method: 'PUT',
    body: JSON.stringify(data)
  })
}

export function getWebsiteProductConfig() {
  return request('/trade/website/product-config')
}

/** 查询官网可售商品目录。 */
export function getWebsiteProducts() {
  return request('/trade/website/products')
}

/** 查询城市合伙人套餐可选的空闲合作区域。 */
export function getWebsiteCityPartnerRegions(params = {}) {
  const query = new URLSearchParams()
  if (params.keyword) query.set('keyword', params.keyword)
  if (params.provinceCode) query.set('provinceCode', params.provinceCode)
  if (params.cityCode) query.set('cityCode', params.cityCode)
  if (params.current) query.set('current', String(params.current))
  if (params.size) query.set('size', String(params.size))
  const queryString = query.toString()
  return request(`/trade/website/city-partner/regions${queryString ? `?${queryString}` : ''}`)
}

/**
 * 上传线下转账支付凭证图片。
 * @param {File} file 支付凭证图片文件
 * @returns {Promise<object>} 上传结果，包含 url 与 filename
 */
export function uploadWebsitePaymentVoucher(file) {
  const formData = new FormData()
  formData.append('file', file)
  return request('/file/upload', {
    method: 'POST',
    body: formData
  })
}

export function getWebsiteOrders() {
  return request('/trade/website/orders')
}

export function getWebsiteOrder(id) {
  const orderId = String(id || '').trim()
  if (!/^\d+$/.test(orderId)) {
    return Promise.reject(new Error('订单ID无效'))
  }
  return request(`/trade/website/orders/${orderId}`)
}

export function createWebsiteAlipayPay(id, terminal) {
  return request(`/trade/website/orders/${id}/pay`, {
    method: 'POST',
    body: JSON.stringify({ terminal })
  })
}

/**
 * 提交线下转账核对信息与支付凭证。
 * @param {string|number} id 订单 ID
 * @param {string} terminal 支付终端
 * @param {string} paymentVoucher 支付凭证文件地址
 * @returns {Promise<object>} 更新后的订单
 */
export function submitWebsiteBankTransfer(id, terminal, paymentVoucher) {
  return request(`/trade/website/orders/${id}/bank-transfer/submit`, {
    method: 'POST',
    body: JSON.stringify({ terminal, paymentVoucher })
  })
}

export function cancelWebsiteOrder(id) {
  return request(`/trade/website/orders/${id}/cancel`, {
    method: 'PUT'
  })
}

export function confirmWebsiteReceipt(id) {
  return request(`/trade/website/orders/${id}/receipt`, {
    method: 'PUT'
  })
}

export function getWebsiteAddresses() {
  return request('/trade/website/addresses')
}

export function deleteWebsiteAddress(id) {
  return request(`/trade/website/addresses/${id}`, {
    method: 'DELETE'
  })
}
